/**
 * Rotating suggested-question pills for the empty help chat panel (#3679).
 *
 * Shows a few of the user's suggested questions at a time (their persona's pool,
 * cut to the pages they can open -- `lib/support/suggestions`), questions about
 * the current page first. Every few seconds the next set fades in (opacity
 * only); rotation pauses while the pointer is over the pills or focus is inside
 * them, and never runs under prefers-reduced-motion. "More" steps to the next
 * set by hand. Each open starts at a random point so repeat users see variety.
 * Picking a pill hands its text to `onAsk`, the same path as typing and sending.
 */
import { useEffect, useMemo, useRef, useState, type FocusEvent } from "react";
import { useLocation } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { useEffectiveAuth } from "../../contexts/auth";
import { suggestedQuestions, suggestionPersona } from "../../lib/support/suggestions";

export const SUGGESTIONS_PER_SET = 6;
export const ROTATE_MS = 6000;
const FADE_MS = 200;

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  } catch {
    return false;
  }
}

interface Props {
  allowedRoutes: readonly string[];
  onAsk: (question: string) => void;
  disabled?: boolean;
}

export function SuggestedQuestions({ allowedRoutes, onAsk, disabled = false }: Props) {
  const { role, preset } = useEffectiveAuth();
  const location = useLocation();
  // One random start per open (the panel remounts this on every new chat).
  const [offset] = useState(() => Math.floor(Math.random() * 1000));
  const questions = useMemo(
    () =>
      suggestedQuestions(suggestionPersona(role, preset), allowedRoutes, location.pathname, offset),
    [role, preset, allowedRoutes, location.pathname, offset],
  );
  const sets = Math.max(1, Math.ceil(questions.length / SUGGESTIONS_PER_SET));
  const [set, setSet] = useState(0);
  const [visible, setVisible] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [reducedMotion] = useState(prefersReducedMotion);
  const fadeTimer = useRef<number | undefined>(undefined);

  const advance = () => {
    if (sets < 2) return;
    window.clearTimeout(fadeTimer.current);
    if (reducedMotion) {
      setSet((s) => (s + 1) % sets);
      return;
    }
    setVisible(false);
    fadeTimer.current = window.setTimeout(() => {
      setSet((s) => (s + 1) % sets);
      setVisible(true);
    }, FADE_MS);
  };
  const advanceRef = useRef(advance);
  advanceRef.current = advance;

  const paused = hovered || focused || reducedMotion || sets < 2;
  useEffect(() => {
    if (paused) return undefined;
    const id = window.setInterval(() => advanceRef.current(), ROTATE_MS);
    return () => window.clearInterval(id);
  }, [paused]);
  useEffect(() => () => window.clearTimeout(fadeTimer.current), []);

  if (questions.length === 0) return null;
  const current = set % sets;
  const shown = questions.slice(
    current * SUGGESTIONS_PER_SET,
    current * SUGGESTIONS_PER_SET + SUGGESTIONS_PER_SET,
  );

  const onBlur = (e: FocusEvent<HTMLElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
  };

  return (
    <section
      aria-label="Suggested questions"
      data-testid="support-suggestions"
      data-paused={paused ? "true" : "false"}
      className="mb-3"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={onBlur}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
          Try asking
        </p>
        {sets > 1 && (
          <button
            type="button"
            onClick={advance}
            aria-label="Show more suggested questions"
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full px-3 text-xs font-medium text-[var(--color-accent)] hover:bg-[var(--color-bg-hover)] transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            More
          </button>
        )}
      </div>
      <ul
        data-testid="support-suggestions-list"
        className={`flex flex-wrap gap-1.5 transition-opacity duration-200 motion-reduce:transition-none ${
          visible ? "opacity-100" : "opacity-0"
        }`}
      >
        {shown.map((q) => (
          // Compact pills that size to their text, so short questions sit
          // several to a row in the narrow panel (#3684); still 44px tall.
          <li key={q} className="min-w-0 max-w-full flex-none">
            <button
              type="button"
              disabled={disabled}
              onClick={() => onAsk(q)}
              className="inline-flex min-h-[44px] max-w-full items-center rounded-full border border-[var(--color-border)] bg-[var(--color-bg-card)] px-2.5 py-1.5 text-left text-xs leading-snug text-[var(--color-text-primary)] hover:border-[var(--color-accent)] hover:bg-[var(--color-accent-subtle)] disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
            >
              {q}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
